/**
 * Send/Run a published flow — single recipient or bulk (paste numbers / upload a
 * CSV-Excel). Triggers the flow by its Flow ID; each send also lands in the Outbox.
 */

import { useState } from 'react';
import { IconSend, IconUpload } from '@tabler/icons-react';
import { FileButton } from '@mantine/core';
import * as XLSX from 'xlsx';
import {
  Drawer, Stack, Group, Text, Button, TextInput, Textarea, Select, SegmentedControl, Badge, Alert, ScrollArea, Divider,
} from '../../../../../components/ui';
import { useWhatsAppFlows, type FlowView, type FlowSendResult } from '../../_shared/useWhatsAppFlows';

function parseNumbersFromSheet(file: File): Promise<string[]> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
        const nums: string[] = [];
        for (const row of rows) {
          for (const cell of row as unknown[]) {
            const s = String(cell ?? '').trim();
            if (/^\+?\d[\d\s-]{6,}$/.test(s)) { nums.push(s); break; }
          }
        }
        resolve(nums);
      } catch { resolve([]); }
    };
    reader.readAsArrayBuffer(file);
  });
}

export function FlowSendDrawer({ flow, opened, onClose }: { flow: FlowView; opened: boolean; onClose: () => void }) {
  const { saving, sendFlow } = useWhatsAppFlows();
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [single, setSingle] = useState('');
  const [bulk, setBulk] = useState('');
  const [bodyText, setBodyText] = useState(flow.defaultBodyText ?? '');
  const [cta, setCta] = useState(flow.defaultCta ?? 'Open');
  const [header, setHeader] = useState(flow.defaultHeaderText ?? '');
  const [footer, setFooter] = useState(flow.defaultFooterText ?? '');
  const [action, setAction] = useState(flow.flowAction ?? 'navigate');
  const [result, setResult] = useState<FlowSendResult | null>(null);

  const recipients = (): string[] => {
    if (mode === 'single') return single.trim() ? [single.trim()] : [];
    return bulk.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  };
  const count = recipients().length;

  const onUpload = async (file: File | null) => {
    if (!file) return;
    const nums = await parseNumbersFromSheet(file);
    setBulk((prev) => [...prev.split(/\n/).map((x) => x.trim()).filter(Boolean), ...nums].join('\n'));
  };

  const handleSend = async () => {
    const list = recipients();
    if (list.length === 0) return;
    const r = await sendFlow(flow.flowId, {
      recipients: list,
      bodyText: bodyText.trim() || undefined,
      cta: cta.trim() || undefined,
      headerText: header.trim() || undefined,
      footerText: footer.trim() || undefined,
      flowAction: action,
    });
    if (r) setResult(r);
  };

  const close = () => { setResult(null); onClose(); };

  return (
    <Drawer opened={opened} onClose={close} position="right" size="md" title={`Send: ${flow.name}`}>
      <Stack gap="md">
        {!flow.metaFlowId && (
          <Alert color="orange">This flow has no Flow ID yet — publish it and save the Flow ID before sending.</Alert>
        )}

        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v as 'single' | 'bulk')}
          data={[{ value: 'single', label: 'Single recipient' }, { value: 'bulk', label: 'Bulk list' }]}
        />

        {mode === 'single' ? (
          <TextInput label="Recipient number" placeholder="+27821234567" value={single} onChange={(e) => setSingle(e.currentTarget.value)} />
        ) : (
          <Stack gap="xs">
            <Textarea label="Recipient numbers" description="One per line (or comma-separated)." placeholder={'+27821234567\n+27839876543'} autosize minRows={4} value={bulk} onChange={(e) => setBulk(e.currentTarget.value)} />
            <Group justify="space-between">
              <FileButton onChange={onUpload} accept=".csv,.xlsx,.xls">
                {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>Upload CSV / Excel</Button>}
              </FileButton>
              <Text size="xs" c="dimmed">{count} recipient(s)</Text>
            </Group>
          </Stack>
        )}

        <Divider label="Message" labelPosition="left" />
        <Textarea label="Body text" placeholder="Tap below to get started." autosize minRows={2} value={bodyText} onChange={(e) => setBodyText(e.currentTarget.value)} />
        <Group grow>
          <TextInput label="Button (CTA)" value={cta} onChange={(e) => setCta(e.currentTarget.value)} />
          <Select label="Action" data={[{ value: 'navigate', label: 'navigate' }, { value: 'data_exchange', label: 'data_exchange' }]} value={action} onChange={(v) => setAction(v || 'navigate')} />
        </Group>
        <Group grow>
          <TextInput label="Header (optional)" value={header} onChange={(e) => setHeader(e.currentTarget.value)} />
          <TextInput label="Footer (optional)" value={footer} onChange={(e) => setFooter(e.currentTarget.value)} />
        </Group>

        {result && (
          <Alert color={result.failed === 0 ? 'green' : 'orange'}>
            <Group gap="xs">
              <Badge color="green" variant="light">{result.sent} sent</Badge>
              {result.failed > 0 && <Badge color="red" variant="light">{result.failed} failed</Badge>}
            </Group>
            {result.results.some((x) => !x.ok) && (
              <ScrollArea h={120} mt="xs">
                <Stack gap={2}>
                  {result.results.filter((x) => !x.ok).map((x, i) => (
                    <Text key={i} size="xs" c="red">{x.toAddress}: {x.error || 'failed'}</Text>
                  ))}
                </Stack>
              </ScrollArea>
            )}
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={close}>Close</Button>
          <Button leftSection={<IconSend size={16} />} onClick={handleSend} loading={saving} disabled={!flow.metaFlowId || count === 0} gradientVariant="primary">
            Send{count > 0 ? ` (${count})` : ''}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
